
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TeacherPaymentHistory } from '../TeacherPaymentHistory';
import { TeacherPaymentStatus } from '../TeacherPaymentStatus';
import { Button } from '@/components/ui/button';
import { PlusCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOfflineState } from '@/hooks/teacher/useOfflineState';
import { format } from 'date-fns';

interface TeacherPaymentsTabProps {
  teacherId: string;
}

export const TeacherPaymentsTab = ({ teacherId }: TeacherPaymentsTabProps) => {
  const [lastPayment, setLastPayment] = useState<{ date: string; amount: number } | undefined>(undefined);
  const [paymentStatus, setPaymentStatus] = useState('Pending');
  const isOffline = useOfflineState();
  const navigate = useNavigate();

  useEffect(() => {
    // Get payment history from localStorage
    const paymentsKey = `teacher_payments_${teacherId}`;
    const paymentsData = localStorage.getItem(paymentsKey);
    if (paymentsData) {
      try {
        const payments = JSON.parse(paymentsData);
        if (Array.isArray(payments) && payments.length > 0) {
          // Sort by date (newest first) and get the first one
          const sortedPayments = [...payments].sort((a, b) => 
            new Date(b.date).getTime() - new Date(a.date).getTime()
          );
          
          if (sortedPayments.length > 0) {
            setLastPayment({
              date: sortedPayments[0].date,
              amount: sortedPayments[0].amount
            });
            
            // Check if there's a payment for the current month
            const currentMonth = format(new Date(), 'MMMM yyyy');
            const currentMonthPayment = sortedPayments.find(p => 
              p.paymentPeriod === currentMonth || 
              (p.date && format(new Date(p.date), 'MMMM yyyy') === currentMonth)
            );
            
            if (currentMonthPayment) {
              setPaymentStatus('Paid');
            } else {
              // Determine status based on last payment
              const lastPaymentDate = new Date(sortedPayments[0].date);
              const currentDate = new Date();
              const monthDiff = (currentDate.getFullYear() - lastPaymentDate.getFullYear()) * 12 + 
                                currentDate.getMonth() - lastPaymentDate.getMonth();
              
              if (monthDiff === 1) {
                setPaymentStatus('Pending');
              } else if (monthDiff > 1) {
                setPaymentStatus('Overdue');
              } else {
                setPaymentStatus('Paid');
              }
            }
          }
        } else {
          setPaymentStatus('Pending');
        }
      } catch (error) {
        console.error('Error parsing teacher payment data:', error);
        setPaymentStatus('Pending');
      }
    } else {
      setPaymentStatus('Pending');
    }
  }, [teacherId]);

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">Payment History</h3>
        <Button 
          onClick={() => navigate(`/teachers/${teacherId}/payment`)}
          disabled={isOffline}
          className="flex items-center gap-2"
        >
          <PlusCircle className="h-4 w-4" />
          New Payment
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <TeacherPaymentStatus status={paymentStatus} lastPayment={lastPayment} />
      </div>
      
      <Card className="p-4">
        <CardContent className="p-0">
          <TeacherPaymentHistory teacherId={teacherId} />
        </CardContent>
      </Card>
    </>
  );
};
